import { useState, useEffect } from 'react';
import { Eye, ArrowLeft, Settings, X } from 'lucide-react';
import { useAdmin } from '../lib/AdminContext';
import { useApp } from '../lib/AppContext';
import { CivicLevel } from '../lib/civicLevelTypes';

const CIVIC_LEVELS: CivicLevel[] = [
  'Observer',
  'Explorer',
  'Sense-Maker',
  'Advocate',
  'Catalyst',
  'Change-Maker',
];

export default function PreviewBar() {
  const {
    viewAsUser,
    toggleViewAsUser,
    previousRoute,
    previousCardId,
    previewCivicLevel,
    setPreviewCivicLevel,
    previewLoggedIn,
    setPreviewLoggedIn,
  } = useAdmin();
  const { navigateTo } = useApp();
  const [shouldNavigateToAdmin, setShouldNavigateToAdmin] = useState(false);
  const [adminRouteToRestore, setAdminRouteToRestore] = useState<string | null>(null);
  const [showMobileSettings, setShowMobileSettings] = useState(false);

  useEffect(() => {
    if (shouldNavigateToAdmin && !viewAsUser) {
      const targetRoute = adminRouteToRestore || previousRoute || 'admin-songs';
      navigateTo(targetRoute as any, previousCardId || undefined);
      setShouldNavigateToAdmin(false);
      setAdminRouteToRestore(null);
    }
  }, [viewAsUser, shouldNavigateToAdmin, navigateTo, adminRouteToRestore, previousRoute, previousCardId]);

  if (!viewAsUser) return null;

  function handleReturnToAdmin() {
    setAdminRouteToRestore(previousRoute);
    toggleViewAsUser();
    setShouldNavigateToAdmin(true);
  }

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[250] bg-[#0A0A0A]/92 backdrop-blur-xl border-b border-[#282828] shadow-2xl transition-all duration-200">
        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#4ECDC4]/40 via-[#4ECDC4]/20 to-transparent" />

        {/* Mobile Layout */}
        <div className="md:hidden px-4 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="relative flex-shrink-0">
                <Eye className="w-4 h-4 text-[#4ECDC4]/80" strokeWidth={2} />
                <div className="absolute inset-0 blur-md bg-[#4ECDC4]/20" />
              </div>
              <span className="text-[13px] font-medium text-[#D0D0D0] truncate">
                Previewing as User
              </span>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowMobileSettings(true)}
                aria-label="Preview settings"
                className="p-1.5 rounded-lg bg-[#1A1A1A]/80 hover:bg-[#202020] border border-[#282828] text-[#A0A0A0] hover:text-[#D0D0D0] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#4ECDC4]/30"
              >
                <Settings className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                onClick={handleReturnToAdmin}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#40E0D0] hover:bg-[#47EAD8] active:bg-[#3DD4C4] text-[#0A0A0A] rounded-lg text-[12px] font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/50"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span className="whitespace-nowrap">Return to Admin</span>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <Eye className="w-4 h-4 text-[#4ECDC4]/80" strokeWidth={2} />
                <div className="absolute inset-0 blur-md bg-[#4ECDC4]/20" />
              </div>
              <span className="text-[13px] font-medium text-[#D0D0D0]">
                Previewing as User
              </span>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="flex items-center bg-[#1A1A1A]/80 rounded-lg p-0.5 border border-[#282828]">
                <button
                  onClick={() => setPreviewLoggedIn(true)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150 ${
                    previewLoggedIn
                      ? 'bg-[#282828] text-[#F5F5F5] shadow-sm'
                      : 'text-[#A0A0A0] hover:text-[#D0D0D0] hover:bg-[#202020]'
                  }`}
                >
                  Logged In
                </button>
                <button
                  onClick={() => setPreviewLoggedIn(false)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150 ${
                    !previewLoggedIn
                      ? 'bg-[#282828] text-[#F5F5F5] shadow-sm'
                      : 'text-[#A0A0A0] hover:text-[#D0D0D0] hover:bg-[#202020]'
                  }`}
                >
                  Logged Out
                </button>
              </div>

              <select
                value={previewCivicLevel}
                onChange={(e) => setPreviewCivicLevel(e.target.value as CivicLevel)}
                className="px-3 py-1.5 bg-[#1A1A1A]/80 hover:bg-[#202020] hover:border-[#303030] border border-[#282828] rounded-lg text-[11px] font-medium text-[#D0D0D0] focus:outline-none focus:ring-2 focus:ring-[#4ECDC4]/30 focus:border-[#4ECDC4]/50 cursor-pointer transition-all duration-150"
              >
                {CIVIC_LEVELS.map((level) => (
                  <option key={level} value={level} className="bg-[#1A1A1A] text-[#F5F5F5]">
                    {level}
                  </option>
                ))}
              </select>

              <button
                onClick={handleReturnToAdmin}
                className="flex items-center gap-2 px-4 py-1.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[12px] font-semibold transition-all duration-150 ml-1 focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/50 focus:ring-offset-2 focus:ring-offset-[#0A0A0A]"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Return to Admin
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Settings Bottom Sheet */}
      {showMobileSettings && (
        <div className="md:hidden fixed inset-0 z-[260] flex items-end" onClick={() => setShowMobileSettings(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full bg-zinc-900/95 border-t border-white/10 rounded-t-2xl shadow-2xl pb-safe"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              <h3 className="text-[16px] font-semibold text-zinc-100">Preview Settings</h3>
              <button
                onClick={() => setShowMobileSettings(false)}
                className="p-2 -mr-2 rounded-lg hover:bg-white/[0.06] transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Auth State Toggle */}
              <div className="space-y-2">
                <label className="text-[13px] font-medium text-zinc-400">Authentication State</label>
                <div className="flex items-center bg-[#1A1A1A]/80 rounded-lg p-0.5 border border-[#282828]">
                  <button
                    onClick={() => {
                      setPreviewLoggedIn(true);
                    }}
                    className={`flex-1 px-3 py-2.5 rounded-md text-[13px] font-medium transition-all duration-150 ${
                      previewLoggedIn
                        ? 'bg-[#282828] text-[#F5F5F5] shadow-sm'
                        : 'text-[#A0A0A0] hover:text-[#D0D0D0] hover:bg-[#202020]'
                    }`}
                  >
                    Signed in
                  </button>
                  <button
                    onClick={() => {
                      setPreviewLoggedIn(false);
                    }}
                    className={`flex-1 px-3 py-2.5 rounded-md text-[13px] font-medium transition-all duration-150 ${
                      !previewLoggedIn
                        ? 'bg-[#282828] text-[#F5F5F5] shadow-sm'
                        : 'text-[#A0A0A0] hover:text-[#D0D0D0] hover:bg-[#202020]'
                    }`}
                  >
                    Signed out
                  </button>
                </div>
              </div>

              {/* Civic Level Selector */}
              <div className="space-y-2">
                <label className="text-[13px] font-medium text-zinc-400">Civic Level</label>
                <select
                  value={previewCivicLevel}
                  onChange={(e) => setPreviewCivicLevel(e.target.value as CivicLevel)}
                  className="w-full px-3 py-2.5 bg-[#1A1A1A]/80 hover:bg-[#202020] hover:border-[#303030] border border-[#282828] rounded-lg text-[13px] font-medium text-[#D0D0D0] focus:outline-none focus:ring-2 focus:ring-[#4ECDC4]/30 focus:border-[#4ECDC4]/50 cursor-pointer transition-all duration-150"
                >
                  {CIVIC_LEVELS.map((level) => (
                    <option key={level} value={level} className="bg-[#1A1A1A] text-[#F5F5F5]">
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reset to Defaults */}
              <button
                onClick={() => {
                  setPreviewLoggedIn(true);
                  setPreviewCivicLevel('Observer');
                }}
                className="w-full py-2 text-[13px] text-[#4ECDC4] hover:text-[#47EAD8] transition-colors font-medium"
              >
                Reset to defaults
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
